import { t } from '../../i18n/I18n';

export interface HydrationTask<T> {
  key: string;
  signature: string;
  label: string;
  kind: 'item' | 'opening' | 'procedural';
  revision?: unknown;
  /** Built before what waits its turn, and without waiting for `admit`: what the walk stands on, say. */
  urgent?: boolean;
  build(): Promise<T | null>;
  apply(value: T): void;
  unavailable?(): void;
}

interface RecordState<T> {
  task: HydrationTask<T>;
  state: 'queued' | 'loading' | 'loaded' | 'missing' | 'failed';
  value?: T;
  error?: string;
  attempts: number;
}

/** A dropped request is ours to sort out, not the customer's: a failed build is re-queued on
 * this schedule before it is ever reported, and stays `queued` — that is, still loading — in
 * between, so a flaky fetch never surfaces as an error the customer is asked to act on. */
const RETRY_DELAYS = [400, 1200];

export interface HydrationProgress {
  status: 'idle' | 'loading' | 'complete' | 'error';
  total: number;
  loaded: number;
  pending: number;
  missing: number;
  failed: number;
  itemsLoaded: number;
  openingsLoaded: number;
  proceduralLoaded: number;
  errors: Array<{ key: string; label: string; message: string }>;
}

export class HydrationQueue<T> {
  private records = new Map<string, RecordState<T>>();
  private active = 0;
  private waiting: RecordState<T>[] = [];
  private disposed = false;
  private reconciled = false;
  private completed: Array<() => void> = [];
  private drainTimer: ReturnType<typeof setTimeout> | undefined;
  private retryTimers = new Set<ReturnType<typeof setTimeout>>();
  private workStarted = 0;
  /** Called each time the last build in hand settles and nothing waits its turn. */
  onIdle?: () => void;

  /** `admit` holds back new builds while it says no — a queue of lesser things waiting for another's to finish;
   * `resume` lets them go once it says yes again. */
  constructor(private readonly release: (value: T) => void, private readonly concurrency = 4,
    private readonly timeoutMs = 30_000, private readonly admit: () => boolean = () => true) {}

  /** Nothing is being built and nothing waits its turn (a failed build waiting to try again does not count). */
  get idle(): boolean {
    return !this.active && this.waiting.every(record => record.state !== 'queued' || this.records.get(record.task.key) !== record);
  }

  resume(): void { this.pump(); }

  reconcile(tasks: HydrationTask<T>[]): void {
    if (this.disposed) return;
    this.reconciled = true;
    const wanted = new Set(tasks.map((task) => task.key));
    for (const [key, record] of this.records) {
      if (wanted.has(key)) continue;
      if (record.value) this.release(record.value);
      this.records.delete(key);
    }
    for (const task of tasks) {
      const old = this.records.get(task.key);
      if (old?.task.signature === task.signature) {
        const unchanged = task.revision !== undefined && Object.is(old.task.revision, task.revision);
        // Still waiting and wanted first now: it goes to the front.
        if (task.urgent && !old.task.urgent && old.state === 'queued') { this.waiting = this.waiting.filter(record => record !== old); this.waiting.unshift(old); }
        old.task = task;
        if (old.value && !unchanged) task.apply(old.value);
      } else {
        const record: RecordState<T> = { task, state: 'queued', value: old?.value, attempts: 0 };
        this.records.set(task.key, record);
        if (task.urgent) this.waiting.unshift(record); else this.waiting.push(record);
      }
    }
    this.pump();
  }

  /**
   * Puts settled records that match back in the queue — for a model that was missing when
   * its item was built and has since arrived. A record still loading is left alone.
   */
  requeue(matches: (task: HydrationTask<T>, state: RecordState<T>['state']) => boolean): number {
    let count = 0;
    for (const record of this.records.values()) {
      if (record.state === 'queued' || record.state === 'loading') continue;
      if (!matches(record.task, record.state)) continue;
      record.state = 'queued'; record.error = undefined; record.attempts = 0; this.waiting.push(record); count++;
    }
    if (count) this.pump();
    return count;
  }
  retry(): void {
    for (const record of this.records.values()) {
      if (record.state !== 'failed') continue;
      record.state = 'queued'; record.error = undefined; record.attempts = 0; this.waiting.push(record);
    }
    this.pump();
  }

  progress(): HydrationProgress {
    const out: HydrationProgress = { status: 'idle', total: this.records.size, loaded: 0, pending: 0,
      missing: 0, failed: 0, itemsLoaded: 0, openingsLoaded: 0, proceduralLoaded: 0, errors: [] };
    for (const record of this.records.values()) {
      if (record.state === 'queued' || record.state === 'loading') out.pending++;
      if (record.state === 'loaded') {
        out.loaded++;
        if (record.task.kind === 'item') out.itemsLoaded++;
        else if (record.task.kind === 'opening') out.openingsLoaded++;
        else out.proceduralLoaded++;
      }
      if (record.state === 'missing') out.missing++;
      if (record.state === 'failed') {
        out.failed++;
        out.errors.push({ key: record.task.key, label: record.task.label, message: record.error ?? 'Unknown error' });
      }
    }
    out.status = out.pending ? 'loading' : out.failed ? 'error' : this.reconciled ? 'complete' : 'idle';
    return out;
  }

  dispose(): void {
    this.disposed = true;
    for (const record of this.records.values()) if (record.value) this.release(record.value);
    this.records.clear(); this.waiting = [];
    if (this.drainTimer !== undefined) clearTimeout(this.drainTimer);
    this.drainTimer = undefined;
    for (const timer of this.retryTimers) clearTimeout(timer);
    this.retryTimers.clear();
    for (const complete of this.completed.splice(0)) complete();
  }

  private drain(): void {
    if (this.drainTimer !== undefined) return;
    if (!this.workStarted) this.workStarted = performance.now();
    while (this.completed.length) {
      if (performance.now() - this.workStarted >= 4) {
        this.drainTimer = setTimeout(() => { this.drainTimer = undefined; this.workStarted = 0; this.drain(); }, 0);
        return;
      }
      this.completed.shift()!();
    }
  }

  private pump(): void {
    if (this.disposed) return;
    while (this.active < this.concurrency && this.waiting.length && (this.waiting[0].task.urgent || this.admit())) {
      const record = this.waiting.shift()!;
      if (record.state !== 'queued' || this.records.get(record.task.key) !== record) continue;
      record.state = 'loading';
      record.attempts++;
      this.active++;
      let settled = false;
      const timer = setTimeout(() => finish(null, new Error(t('#ViewModelLoadTimedOut'))), this.timeoutMs);
      const finish = (value: T | null, error?: unknown) => {
        if (settled) { if (value) this.release(value); return; }
        settled = true;
        clearTimeout(timer);
        this.completed.push(() => {
        this.active--;
        if (this.disposed || this.records.get(record.task.key) !== record) {
          if (value) this.release(value);
        } else if (error) {
          record.error = error instanceof Error ? error.message : String(error);
          this.reattempt(record);
        } else {
          if (record.value && record.value !== value) this.release(record.value);
          record.value = value ?? undefined;
          record.state = value ? 'loaded' : 'missing';
          try {
            if (value) record.task.apply(value); else record.task.unavailable?.();
          } catch (error) {
            record.error = error instanceof Error ? error.message : String(error);
            record.state = 'failed';
          }
        }
        this.pump();
        if (!this.disposed && this.idle) this.onIdle?.();
        });
        this.drain();
      };
      Promise.resolve().then(() => record.task.build()).then((value) => finish(value), (error) => finish(null, error));
    }
  }

  /** Give the build another go before admitting the failure, or give up and report it. */
  private reattempt(record: RecordState<T>): void {
    const delay = RETRY_DELAYS[record.attempts - 1];
    if (delay === undefined) { record.state = 'failed'; return; }
    record.state = 'queued';
    const timer = setTimeout(() => {
      this.retryTimers.delete(timer);
      if (this.disposed || this.records.get(record.task.key) !== record || record.state !== 'queued') return;
      this.waiting.push(record); this.pump();
    }, delay);
    this.retryTimers.add(timer);
  }
}
